/**
 * Collectible Texture Generator
 *
 * PHASE 7A: Convert Collectibles from Graphics to Sprites
 * Pre-generates all collectible textures at boot time for GPU caching.
 *
 * SPRITE ASSET SUPPORT:
 * - Loads PNG sprites from public/sprites/collectibles/ if available
 * - Falls back to procedural generation if PNG not found
 * - Allows gradual sprite replacement and external editing
 *
 * Performance Impact:
 * - Before: 10-20 collectibles × 60 FPS × 4-8 draw calls = 2,400-9,600 ops/sec
 * - After: 10-20 sprites × 60 FPS × 1 GPU blit = 600-1,200 ops/sec
 * - Expected gain: 15-25 FPS on mobile devices
 */

import Phaser from 'phaser';
import { TextureGenerator } from '@/utils/TextureGenerator';
import { DecorationDrawingHelpers } from './decorators/DecorationDrawingHelpers';
import { SeededRandom } from '@/utils/SeededRandom';
import { CollectibleType } from '@/entities/Collectible';

export class CollectibleTextureGenerator {
  private static readonly TEXTURE_SIZE = 64; // Size of texture canvas
  private static readonly TEXTURE_PADDING = 8; // Padding around drawing
  private static readonly SPRITE_PATH = 'sprites/collectibles/'; // Path to sprite assets
  private static loadedFromAssets: Set<string> = new Set(); // Track which sprites loaded from PNG

  /**
   * Preload sprite assets from PNG sprite sheets and JSON atlases
   * Call this in BootScene.preload() before generating textures
   */
  static preloadSpriteAssets(scene: Phaser.Scene): void {
    console.log('📦 [TextureGen] Preloading collectible sprite sheets...');

    const types = Object.values(CollectibleType);

    for (const type of types) {
      const textureKey = this.getTextureKey(type);
      const pngPath = `${this.SPRITE_PATH}${type}.png`;
      const jsonPath = `${this.SPRITE_PATH}${type}.json`;

      // Load sprite sheet with atlas (Phaser will handle missing files gracefully)
      scene.load.atlas(textureKey, pngPath, jsonPath);
    }
  }

  /**
   * Generate all collectible textures
   * Call this once at boot time (BootScene or GameScene.create())
   * Only generates procedurally for sprites that weren't loaded from PNG files
   */
  static generateAllTextures(scene: Phaser.Scene): void {
    console.log('🎨 [TextureGen] Generating collectible textures...');
    const startTime = Date.now();

    // Track statistics
    let loadedFromPNG = 0;
    let generatedProcedurally = 0;

    const types = [
      // Zone 1 Collectibles
      CollectibleType.FIREFLY,
      CollectibleType.SNAIL,
      CollectibleType.FROG,
      CollectibleType.BUTTERFLY,
      CollectibleType.BEETLE,
      CollectibleType.DEWDROP,
      CollectibleType.MUSHROOM,
      // Zone 2 Collectibles
      CollectibleType.BIRD,
      CollectibleType.MOTH,
      // Zone 3 Collectibles
      CollectibleType.FEATHER,
      // Zone 4 Collectibles
      CollectibleType.STAR,
      CollectibleType.STARDUST,
      // Zone 5 Collectibles
      CollectibleType.COIN,
      CollectibleType.GEM,
      CollectibleType.SPARKLE,
      // Zone 6 Collectibles
      CollectibleType.ENERGY_ORB,
      CollectibleType.COSMIC,
      CollectibleType.PORTAL,
    ];

    for (const type of types) {
      const textureKey = this.getTextureKey(type);

      // Check if texture was loaded from PNG asset
      if (scene.textures.exists(textureKey)) {
        const texture = scene.textures.get(textureKey);

        // Verify it's not the missing texture placeholder
        if (texture && texture.key !== '__MISSING') {
          this.loadedFromAssets.add(type);
          loadedFromPNG++;
          continue;
        }
      }

      // Generate procedurally if not loaded from asset
      this.generateCollectibleTexture(scene, type);
      generatedProcedurally++;
    }

    const duration = Date.now() - startTime;
    console.log(
      `✅ [TextureGen] Collectible textures ready in ${duration}ms ` +
        `(${loadedFromPNG} from PNG, ${generatedProcedurally} procedural)`
    );

    if (loadedFromPNG > 0) {
      console.log(`   📁 Loaded from assets: ${Array.from(this.loadedFromAssets).join(', ')}`);
    }
  }

  /**
   * Generate a single collectible texture
   */
  private static generateCollectibleTexture(scene: Phaser.Scene, type: CollectibleType): void {
    const textureKey = this.getTextureKey(type);
    const size = this.TEXTURE_SIZE;
    const padding = this.TEXTURE_PADDING;

    // Create a deterministic random for consistent texture generation
    const random = new SeededRandom(this.getTypeSeed(type));

    TextureGenerator.generateTexture(scene, textureKey, size, size, graphics => {
      // Center the drawing in the texture
      const centerX = size / 2;
      const centerY = size / 2;
      const width = size - padding * 2;
      const height = size - padding * 2;

      // Translate graphics to center
      graphics.translateCanvas(centerX, centerY);

      // Draw the collectible using existing drawing helpers
      this.drawCollectible(graphics, type, random, width, height);
    });
  }

  /**
   * Draw a collectible using the existing DecorationDrawingHelpers
   */
  private static drawCollectible(
    graphics: Phaser.GameObjects.Graphics,
    type: CollectibleType,
    random: SeededRandom,
    width: number,
    height: number
  ): void {
    // Map collectible types to drawing helpers
    // Note: We're reusing the decoration drawing code for consistency
    switch (type) {
      case CollectibleType.FIREFLY:
        DecorationDrawingHelpers.drawFirefly(graphics, random, width, height);
        break;
      case CollectibleType.SNAIL:
        DecorationDrawingHelpers.drawTinySnail(graphics, random, width, height);
        break;
      case CollectibleType.FROG:
        DecorationDrawingHelpers.drawSmallFrog(graphics, random, width, height);
        break;
      case CollectibleType.BUTTERFLY:
        DecorationDrawingHelpers.drawButterfly(graphics, random, width, height);
        break;
      case CollectibleType.BEETLE:
        DecorationDrawingHelpers.drawBeetle(graphics, random, width, height);
        break;
      case CollectibleType.DEWDROP:
        DecorationDrawingHelpers.drawDewdrop(graphics, random, width, height);
        break;
      case CollectibleType.MUSHROOM:
        DecorationDrawingHelpers.drawMushroomBuddy(graphics, random, width, height);
        break;
      // TODO: Add Zone 2-6 collectible drawings (using placeholder for now)
      default:
        // Fallback: draw a simple circle
        graphics.fillStyle(0xffff00, 1);
        graphics.fillCircle(0, 0, 12);
        break;
    }
  }

  /**
   * Get texture key for a collectible type
   */
  static getTextureKey(type: CollectibleType): string {
    return `collectible_${type}`;
  }

  /**
   * Get deterministic seed for a collectible type
   */
  private static getTypeSeed(type: CollectibleType): number {
    // Convert type string to a deterministic seed
    let seed = 0;
    for (let i = 0; i < type.length; i++) {
      seed = (seed << 5) - seed + type.charCodeAt(i);
      seed = seed & seed; // Convert to 32-bit integer
    }
    return Math.abs(seed);
  }

  /**
   * Check if a collectible type is loaded from a PNG asset
   */
  static isLoadedFromAsset(type: CollectibleType): boolean {
    return this.loadedFromAssets.has(type);
  }

  /**
   * Get list of all collectible types loaded from PNG assets
   */
  static getAssetLoadedTypes(): CollectibleType[] {
    return Array.from(this.loadedFromAssets) as CollectibleType[];
  }

  /**
   * Clear all collectible textures
   */
  static clearTextures(scene: Phaser.Scene): void {
    const keys = Object.values(CollectibleType).map(type => this.getTextureKey(type));
    TextureGenerator.clearTextures(scene, keys);
    this.loadedFromAssets.clear();
  }
}
